import { Component, Injector, OnInit, ViewChild, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { uniqueId } from 'lodash';
import {
  ButtonComponent,
  FormsUtil,
  InputButton,
  InputComponent,
  InputType,
  MbsValidators,
  ModalComponent,
  ModalFooterDirective
} from 'mbs-ui-kit';
import { CompanySelectComponent } from 'mbs-ui-shared@components/paged-select/company-select.component';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { UsersFacade } from 'mbs-ui-shared@facades/users.facade';
import { ComputersAbstractService } from 'mbs-ui-shared@services/computers.service';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-user-company-modal',
  templateUrl: './add-user-modal.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    I18NextPipe,
    ModalComponent,
    InputComponent,
    ModalFooterDirective,
    CompanySelectComponent
  ]
})
export class AddUserModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private usersFacade = inject(UsersFacade);
  private injector = inject(Injector);
  private computersService = inject(ComputersAbstractService);
  private auth = inject(AuthFacade);

  public appendButtons: InputButton[] = [
    {
      type: 'secondary',
      id: 'user-password-visibility',
      icon: 'fa fa-eye',
      loading: false
    }
  ];
  public modalForm: UntypedFormGroup = this.fb.group({
    company: [null, [Validators.required]],
    userName: [null, [Validators.required]],
    password: [
      null,
      {
        validators: [Validators.required],
        asyncValidators: [MbsValidators.passwordStrengthValidator(this.auth)]
      }
    ]
  });
  public inputType: InputType = InputType.Password;
  public requestInProcess = false;
  public initialLoading = true;
  public passwordGenerationInProgress = false;
  public preselectedCompanyId: string = null;

  /**
   * ng-select resolves `appendTo` via `document.querySelector`, so a shared selector like `.modal-content`
   * puts the dropdown into the first modal in the DOM instead of this one when modals are stacked.
   * `.modal-content` is an ancestor of this modal, hence `:has` instead of a descendant selector.
   */
  public readonly modalId = uniqueId('mbs-add-user-modal-');
  public readonly companySelectAppendTo = `.modal-content:has(#${this.modalId})`;

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  ngOnInit() {
    this.usersFacade.loadData();

    // A company passed from the outside is shown as a preselected, locked control: the user sees it but cannot change it.
    if (this.baseModal.data?.companyId) {
      this.preselectedCompanyId = this.baseModal.data.companyId;
      this.modalForm.get('company').disable();
    }

    if (this.baseModal.data?.hid) {
      this.computersService
        .suggestUserName(this.baseModal.data.hid, this.baseModal.data.companyId)
        .pipe(
          untilDestroyed(this),
          catchError(() => of(null))
        )
        .subscribe({
          next: ({ userName }) => {
            userName && this.modalForm.get('userName').patchValue(userName);
            this.initialLoading = false;
          }
        });
    } else {
      this.initialLoading = false;
    }
  }

  public onChangePasswordTypeButtonClick() {
    this.inputType = this.inputType === InputType.Password ? InputType.Text : InputType.Password;
    this.appendButtons = this.appendButtons.map((button) => {
      if (button.id === 'user-password-visibility') {
        return {
          ...button,
          icon: this.inputType === InputType.Password ? 'fa fa-eye' : 'fa fa-eye-slash'
        };
      }

      return button;
    });
  }

  public setDefaultPassword() {
    this.passwordGenerationInProgress = true;
    this.auth
      .generatePassword()
      .pipe(catchError(() => of('')))
      .subscribe((password) => {
        this.passwordGenerationInProgress = false;
        this.modalForm.get('password').patchValue(password);
      });
  }

  public onCreateUserButtonClick(): void {
    if (!this.modalForm.valid) {
      FormsUtil.triggerValidation(this.modalForm);

      return;
    }

    const { userName, password, company } = this.modalForm.value;
    this.requestInProcess = true;

    this.usersFacade
      .create({
        name: userName,
        password,
        companyId: this.baseModal.data?.companyId ?? company?.id,
        enabled: true
      })
      .pipe(
        switchMap((user) =>
          toObservable(this.usersFacade.data, { injector: this.injector }).pipe(
            filter((users) => users.some((u) => u.id === user.id)),
            take(1),
            map(() => user)
          )
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: (user) => this.baseModal.save(user),
        error: () => (this.requestInProcess = false)
      });
  }
}
